import { useState, useEffect, useCallback, useMemo } from 'react';
import { subDays } from 'date-fns';
import { toLocalDateStr } from '../lib/utils';
import { createConfluenceService } from '@/services/confluence-service';
import { atlassianOAuth2 } from '@/services/atlassian-oauth2';
import type { ConfluenceSpace } from '@/services/types';
import { ConfluenceIndicators } from './ConfluenceIndicators';
import { ConfluencePageDetail } from './ConfluencePageDetail';
import { ConfluenceSpaceCombobox } from './ConfluenceSpaceCombobox';
import * as audit from '@/services/audit';
import * as historyStore from '@/services/analysis-history-store';
import { generateConfluencePDFReport, downloadPDF } from '@/services/pdf-report';
import { saveReport } from '@/services/storage';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowTrendUp,
  faBook,
  faCalendarDays,
  faChartBar,
  faChartPie,
  faChevronDown,
  faChevronRight,
  faCircleExclamation,
  faCirclePlus,
  faCodeCompare,
  faComment,
  faFileLines,
  faGear,
  faGlobe,
  faMagnifyingGlass,
  faPenToSquare,
  faPencil,
  faRotate,
  faSpinner,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';

export type ConfluenceEventType = 'page_created' | 'page_edited' | 'minor_edit' | 'comment_added';

export interface ConfluencePageEvent {
  id: string; timestamp: string; pageId: string; pageTitle: string;
  pageUrl: string; spaceKey: string; spaceName: string;
  authorId: string; authorName: string; version: number; isNew: boolean;
  eventType: ConfluenceEventType;
  minorEdit?: boolean;
  versionMessage?: string;
  commentId?: string;
  commentVersion?: number;
  commentBody?: string;
  commentType?: 'footer' | 'inline';
}

export interface ConfluenceActivityData {
  events: ConfluencePageEvent[];
  authors: { id: string; name: string; count: number }[];
  dailyCounts: { date: string; edits: number; created: number }[];
  totalPages: number; uniqueContributors: number;
  dateRange: { start: string; end: string };
}

export interface ConfluenceReviewData {
  activityData: ConfluenceActivityData;
  selectedSpace: string;
  customFrom: string;
  customTo: string;
}

interface Props { onClose: () => void; embedded?: boolean; inline?: boolean; onOpenSettings?: () => void; reviewData?: ConfluenceReviewData; }

function isScopeError(msg: string) {
  return msg.includes('scope does not match') || msg.includes('Unauthorized') || msg.includes('401');
}

// ==================== Comment LCS diff helpers ====================

type DiffLine = { type: 'added' | 'removed' | 'unchanged'; text: string };

function computeCommentDiff(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split(/\s+/).filter(Boolean).slice(0, 400);
  const b = newText.split(/\s+/).filter(Boolean).slice(0, 400);
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);
  const result: DiffLine[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i-1] === b[j-1]) { result.unshift({ type: 'unchanged', text: a[i-1] }); i--; j--; }
    else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) { result.unshift({ type: 'added', text: b[j-1] }); j--; }
    else { result.unshift({ type: 'removed', text: a[i-1] }); i--; }
  }
  return result;
}

// ==================== CommentEventDetail ====================

export interface CommentEventProps {
  commentId?: string;
  commentType?: 'footer' | 'inline';
  commentVersion?: number;
  commentBody?: string;
  version?: number;
}

export function CommentEventDetail({ event }: { event: CommentEventProps }) {
  const [diffLines, setDiffLines] = useState<DiffLine[] | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);

  const commentVersion = event.commentVersion ?? event.version ?? 1;

  useEffect(() => {
    if (!event.commentId || !event.commentType || commentVersion <= 1) return;
    setDiffLoading(true);
    setDiffError(null);
    (async () => {
      try {
        const svc = await createConfluenceService();
        if (!svc) throw new Error('Not connected');
        const prevBody = await svc.getCommentVersionBody(event.commentId!, event.commentType!, commentVersion - 1);
        const currBody = event.commentBody ?? '';
        setDiffLines(computeCommentDiff(prevBody, currBody));
      } catch (e: any) {
        setDiffError(e.message || 'Failed to load diff');
      } finally {
        setDiffLoading(false);
      }
    })();
  }, [event.commentId, event.commentType, commentVersion, event.commentBody]);

  return (
    <div className="border-t border-gray-100 dark:border-slate-700/30 bg-gray-50/50 dark:bg-slate-800/20 px-4 py-3 space-y-2">
      {/* Current comment body */}
      {event.commentBody ? (
        <div className="text-sm text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-800/50 rounded-lg px-3 py-2 border border-gray-200 dark:border-slate-700/40 whitespace-pre-wrap">
          {event.commentBody}
        </div>
      ) : (
        <span className="text-xs text-gray-400 dark:text-slate-500 italic">No comment content available</span>
      )}

      {/* Version indicator + diff */}
      {commentVersion > 1 && (
        <div className="border border-gray-200 dark:border-slate-700/40 rounded-lg overflow-hidden">
          <div
            role="button" tabIndex={0}
            onClick={() => setDiffOpen(v => !v)}
            onKeyDown={e => e.key === 'Enter' && setDiffOpen(v => !v)}
            className="px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors cursor-pointer select-none"
          >
            <div className="flex items-center gap-1.5">
              <FontAwesomeIcon icon={faCodeCompare} className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-medium text-gray-700 dark:text-slate-300">
                Changes v{commentVersion - 1} → v{commentVersion}
              </span>
              {diffLoading && <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin text-gray-400" />}
              {diffLines && (
                <span className="text-[10px] text-gray-400 dark:text-slate-500">
                  (<span className="text-emerald-500">+{diffLines.filter(l => l.type === 'added').length}</span>
                  {' / '}
                  <span className="text-rose-500">-{diffLines.filter(l => l.type === 'removed').length}</span>)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {diffLines && (
                <button
                  onClick={e => { e.stopPropagation(); setShowUnchanged(v => !v); }}
                  className="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600"
                >
                  {showUnchanged ? 'Hide unchanged' : 'Show all'}
                </button>
              )}
              {diffOpen
                ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-gray-400" />
                : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-gray-400" />}
            </div>
          </div>
          {diffOpen && (
            <div className="border-t border-gray-200 dark:border-slate-700/40">
              {diffError && (
                <div className="flex items-center gap-2 px-3 py-3 text-xs text-red-500">
                  <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5" /> {diffError}
                </div>
              )}
              {diffLines && (
                <div className="overflow-x-auto font-mono text-[11px] max-h-64 overflow-y-auto">
                  {diffLines
                    .filter(l => showUnchanged || l.type !== 'unchanged')
                    .map((line, idx) => (
                      <div key={idx} className={
                        line.type === 'added'
                          ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-3 py-0.5 whitespace-pre-wrap'
                          : line.type === 'removed'
                          ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 px-3 py-0.5 whitespace-pre-wrap'
                          : 'text-gray-400 dark:text-slate-600 px-3 py-0.5 whitespace-pre-wrap'
                      }>
                        <span className="select-none mr-2 opacity-50">
                          {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
                        </span>
                        {line.text}
                      </div>
                    ))}
                  {diffLines.every(l => l.type === 'unchanged') && (
                    <div className="px-3 py-4 text-xs text-gray-400 dark:text-slate-500 text-center italic">
                      No text changes between these versions
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const COLORS = ['#3b82f6','#10b981','#f59e0b','#a855f7','#f43f5e','#06b6d4'];


export function ConfluenceActivityView({ onClose, embedded, inline, onOpenSettings, reviewData }: Props) {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [spaces, setSpaces] = useState<ConfluenceSpace[]>([]);
  const [selectedSpace, setSelectedSpace] = useState('');
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [customFrom, setCustomFrom] = useState(() => toLocalDateStr(subDays(new Date(), 30)));
  const [customTo, setCustomTo] = useState(() => toLocalDateStr());
  const [datePreset, setDatePreset] = useState(30);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [data, setData] = useState<ConfluenceActivityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cloudName, setCloudName] = useState('');
  const [showCharts, setShowCharts] = useState(true);
  const [filterAuthor, setFilterAuthor] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [showCreated, setShowCreated] = useState(true);
  const [showEdits, setShowEdits] = useState(true);
  const [indicatorTrigger, setIndicatorTrigger] = useState(0);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfSaved, setPdfSaved] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const [selectedPage, setSelectedPage] = useState<{ pageId: string; spaceKey: string; rowKey: string } | null>(null);

  // Hydrate from saved snapshot (reopen from ReportsView)
  useEffect(() => {
    if (!reviewData) return;
    setData(reviewData.activityData);
    setSelectedSpace(reviewData.selectedSpace);
    setCustomFrom(reviewData.customFrom);
    setCustomTo(reviewData.customTo);
    setIsReview(true);
    setExpandedDates(new Set([toLocalDateStr(), toLocalDateStr(subDays(new Date(), 1))]));
  }, [reviewData]);

  const lookbackDays = Math.max(1, Math.ceil(
    (new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86_400_000
  ));

  const ts = useMemo(() => ({
    backgroundColor: isDark ? '#1e293b' : '#fff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
    borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111',
  }), [isDark]);

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    atlassianOAuth2.getState().then(s => {
      if (s.token?.cloudName) setCloudName(s.token.cloudName);
    });
    (async () => {
      setLoadingSpaces(true);
      try {
        const svc = await createConfluenceService();
        if (!svc) { setError('NOT_CONNECTED'); return; }
        const { spaces: list } = await svc.getSpaces();
        const current = list.filter(s => s.status === 'current');
        setSpaces(current);
        // Resolve real Atlassian display names for personal space owners in background
        const resolved = await svc.resolvePersonalSpaceNames(current);
        setSpaces(resolved);
      } catch (e: any) { setError(isScopeError(e.message) ? 'SCOPE_ERROR' : e.message); }
      finally { setLoadingSpaces(false); }
    })();
  }, []);

  const buildActivity = useCallback((pages: any[], nameMap: Map<string, string>) => {
    const events: ConfluencePageEvent[] = pages.map((p: any) => {
      const isNew = p.version === 1 && !p.commentId;
      const eventType: ConfluenceEventType = p.eventType ?? (isNew ? 'page_created' : p.minorEdit ? 'minor_edit' : 'page_edited');
      // Use commentId + commentVersion for unique event ID when it's a comment event
      const id = p.commentId
        ? `comment-${p.commentId}-v${p.commentVersion ?? p.version ?? 1}`
        : `${p.id}-v${p.version}`;
      return {
        id, timestamp: p.updatedAt, pageId: p.id, pageTitle: p.title,
        pageUrl: p.url, spaceKey: p.spaceKey, spaceName: p.spaceName ?? p.spaceKey,
        authorId: p.authorId, authorName: nameMap.get(p.authorId) ?? p.authorId,
        version: p.version, isNew,
        eventType,
        minorEdit: p.minorEdit,
        versionMessage: p.versionMessage,
        commentId: p.commentId,
        commentVersion: p.commentVersion,
        commentBody: p.commentBody,
        commentType: p.commentType,
      };
    });
    const dayMap = new Map<string, { date: string; _ts: number; edits: number; created: number }>();
    for (const e of events) {
      const d = new Date(e.timestamp);
      const key = toLocalDateStr(d);
      if (!dayMap.has(key)) dayMap.set(key, {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        _ts: new Date(key).getTime(), edits: 0, created: 0,
      });
      const slot = dayMap.get(key)!;
      if (e.isNew) slot.created++; else slot.edits++;
    }
    const dailyCounts = Array.from(dayMap.values()).sort((a, b) => a._ts - b._ts).map(({ _ts: _, ...r }) => r);
    const authorMap = new Map<string, { id: string; name: string; count: number }>();
    for (const e of events) {
      if (!authorMap.has(e.authorId)) authorMap.set(e.authorId, { id: e.authorId, name: e.authorName, count: 0 });
      authorMap.get(e.authorId)!.count++;
    }
    return { events, dailyCounts, authors: Array.from(authorMap.values()).sort((a, b) => b.count - a.count) };
  }, []);

  const fetchActivity = useCallback(async () => {
    if (!selectedSpace) { setError('Please select a space'); return; }
    setLoading(true); setError(null); setPdfSaved(false);
    const t0 = Date.now();
    audit.auditAnalysisStarted('confluence', { space: selectedSpace });
    try {
      const svc = await createConfluenceService();
      if (!svc) throw new Error('Not connected to Confluence');

      const isAllSpaces = selectedSpace === '__ALL__';
      let allPages: any[] = await svc.getPagesForSpaceOrAll(selectedSpace, customFrom, customTo);

      // Expand each page into its individual version events within the date range
      const versionEvents = await svc.expandPagesToVersionEvents(allPages, customFrom, customTo);

      const ids = [...new Set(versionEvents.map((p: any) => p.authorId).filter(Boolean))];
      const nameMap = await svc.resolveUserNames(ids);
      const { events, dailyCounts, authors } = buildActivity(versionEvents, nameMap);

      const newData: ConfluenceActivityData = {
        events, dailyCounts, authors,
        totalPages: allPages.length, uniqueContributors: authors.length,
        dateRange: { start: customFrom, end: customTo },
      };
      setData(newData);
      setIndicatorTrigger(t => t + 1);
      setExpandedDates(new Set([toLocalDateStr(), toLocalDateStr(subDays(new Date(), 1))]));

      const durationMs = Date.now() - t0;
      const spaceLabel = isAllSpaces ? 'All Spaces' : (spaces.find(s => s.key === selectedSpace)?.name ?? selectedSpace);
      audit.auditAnalysisCompleted('confluence', durationMs, { space: selectedSpace, pageCount: allPages.length });

      // Save snapshot to analysis history store
      await historyStore.saveSnapshot({
        id: `confluence-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        mode: 'confluence',
        title: `Confluence — ${spaceLabel}`,
        timestamp: new Date().toISOString(),
        durationMs,
        success: true,
        summary: {
          commitCount: 0,
          eventCount: allPages.length,
          authorCount: authors.length,
          dateRange: { start: customFrom, end: customTo },
          extra: { spaceKey: selectedSpace, spaceName: spaceLabel },
        },
      }, newData);
    } catch (e: any) {
      setError(e.message ?? 'Failed to fetch Confluence activity');
      audit.auditAnalysisFailed('confluence', e.message, { space: selectedSpace });
    } finally { setLoading(false); }
  }, [selectedSpace, customFrom, customTo, buildActivity, spaces]);

  const handleGeneratePdf = useCallback(async () => {
    if (!data) return;
    setGeneratingPdf(true);
    try {
      const spaceLabel = selectedSpace === '__ALL__'
        ? 'All Spaces'
        : spaces.find(s => s.key === selectedSpace)?.name ?? selectedSpace;
      const pagesPerDay = +(data.totalPages / Math.max(lookbackDays, 1)).toFixed(2);
      const newPages = data.events.filter(e => e.isNew).length;
      const newRatio = data.totalPages > 0 ? Math.round((newPages / data.totalPages) * 100) : 0;
      const activeDays = new Set(data.events.map(e => e.timestamp.slice(0, 10))).size;
      const activeDaysPct = Math.round((activeDays / Math.max(lookbackDays, 1)) * 100);
      const reportData = {
        generatedAt: new Date().toISOString(),
        dateRange: data.dateRange,
        spaceName: spaceLabel,
        spaceKey: selectedSpace === '__ALL__' ? 'ALL' : selectedSpace,
        cloudName,
        totalPages: data.totalPages,
        uniqueContributors: data.uniqueContributors,
        pagesPerDay,
        newPagesRatio: newRatio,
        activeDaysPct,
        authors: data.authors.map(a => ({ name: a.name, count: a.count })),
        dailyCounts: data.dailyCounts,
        topPages: data.events.slice(0, 50).map(e => ({
          title: e.pageTitle, author: e.authorName, version: e.version, isNew: e.isNew,
        })),
        indicators: [],
      };
      const pdfBytes = generateConfluencePDFReport(reportData);
      const filename = `verigit-confluence-${(selectedSpace).toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}.pdf`;
      downloadPDF(pdfBytes, filename);
      // Save to Reports store
      await saveReport({
        id: `report-confluence-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString(),
        type: 'confluence',
        filename,
        title: `Confluence Report — ${spaceLabel}`,
        description: `${data.totalPages} pages, ${data.uniqueContributors} contributors`,
        sizeBytes: pdfBytes.byteLength,
        pdfBlob: new Uint8Array(pdfBytes),
        metadata: { spaceKey: selectedSpace, spaceName: spaceLabel, dateRange: data.dateRange, totalPages: data.totalPages },
      });
      setPdfSaved(true);
    } catch (e: any) {
      console.error('PDF generation failed:', e);
    } finally { setGeneratingPdf(false); }
  }, [data, selectedSpace, spaces, lookbackDays, cloudName]);

  const filteredEvents = useMemo(() => {
    if (!data) return [];
    return data.events.filter(e => {
      if (filterAuthor && e.authorName !== filterAuthor) return false;
      if (!showCreated && e.eventType === 'page_created') return false;
      if (!showEdits && e.eventType !== 'page_created') return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!e.pageTitle.toLowerCase().includes(q) && !e.authorName.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [data, filterAuthor, filterSearch, showCreated, showEdits]);

  const groupedByDate = useMemo(() => {
    const groups = new Map<string, ConfluencePageEvent[]>();
    for (const e of filteredEvents) {
      const key = toLocalDateStr(new Date(e.timestamp));
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(e);
    }
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredEvents]);

  const toggleDate = (d: string) => setExpandedDates(prev => {
    const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n;
  });

  const barData = useMemo(() =>
    data?.authors.map(a => ({ name: a.name.split(' ')[0], fullName: a.name, pages: a.count })) ?? []
  , [data]);

  const pieData = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'New', value: data.events.filter(e => e.isNew).length, fill: '#10b981' },
      { name: 'Edits', value: data.events.filter(e => !e.isNew).length, fill: '#3b82f6' },
    ].filter(d => d.value > 0);
  }, [data]);

  const depthData = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'v1', count: data.events.filter(e => e.version === 1).length },
      { name: 'v2-5', count: data.events.filter(e => e.version >= 2 && e.version <= 5).length },
      { name: 'v6-15', count: data.events.filter(e => e.version >= 6 && e.version <= 15).length },
      { name: 'v16+', count: data.events.filter(e => e.version > 15).length },
    ].filter(b => b.count > 0);
  }, [data]);

  const wrap = embedded
    ? 'h-full overflow-y-auto'
    : inline
    ? 'h-full overflow-y-auto'
    : 'fixed inset-0 z-[100] bg-white dark:bg-slate-900 overflow-y-auto';

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={wrap}>
      {/* Header */}
      {!inline && (
        <div className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-slate-700/50">
          <div className={`${embedded ? 'px-4 py-3' : 'px-6 py-4'} flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              {!embedded && (
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                  <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5 text-gray-500 dark:text-slate-400" />
                </button>
              )}
              <FontAwesomeIcon icon={faBook} className="h-5 w-5 text-blue-400" />
              <div>
                <h1 className={`${embedded ? 'text-sm' : 'text-lg'} font-semibold text-gray-900 dark:text-white`}>Confluence Activity</h1>
                {cloudName && <p className="text-xs text-gray-500 dark:text-slate-400">{cloudName}</p>}
              </div>
            </div>
            {data && <span className="text-xs text-gray-400 dark:text-slate-500">{filteredEvents.length} events</span>}
          </div>
        </div>
      )}

      {isReview && (
        <div className={`px-4 py-2 text-xs font-medium text-center ${isDark ? 'bg-amber-500/10 text-amber-300 border-b border-amber-500/20' : 'bg-amber-50 text-amber-700 border-b border-amber-200'}`}>
          Reviewing saved analysis — {customFrom} to {customTo}
        </div>
      )}

      <div className="px-6 py-6 space-y-6">
        {/* Config panel */}
        <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faGlobe} className="h-3.5 w-3.5" /> Space
              </label>
              {loadingSpaces ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-2.5"><FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Loading spaces…</div>
              ) : (
                <ConfluenceSpaceCombobox
                  value={selectedSpace}
                  onChange={setSelectedSpace}
                  spaces={spaces}
                  isDark={isDark}
                  showAllOption
                />
              )}
            </div>
            <div className="md:col-span-3">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">
                  <FontAwesomeIcon icon={faCalendarDays} className="h-3.5 w-3.5" /> Time Range
                </label>
                <button onClick={() => setShowDatePicker(v => !v)}
                  className={`text-[10px] px-2 py-0.5 rounded ${showDatePicker ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-500 dark:text-slate-500'}`}>
                  {showDatePicker ? 'Hide' : 'Custom'}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-1">
                {[7,14,30,60,90,180].map(days => (
                  <button key={days} onClick={() => { setDatePreset(days); setCustomTo(toLocalDateStr()); setCustomFrom(toLocalDateStr(subDays(new Date(), days))); setShowDatePicker(false); }}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${datePreset === days && !showDatePicker ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' : 'bg-gray-100 dark:bg-slate-700/30 text-gray-500 dark:text-slate-400 border-transparent hover:text-gray-900 dark:hover:text-white'}`}>
                    {days === 7 ? '7d' : days === 14 ? '2w' : days === 30 ? '30d' : days === 60 ? '60d' : days === 90 ? '90d' : '6mo'}
                  </button>
                ))}
              </div>
              {showDatePicker && (
                <div className="flex gap-2 items-center mt-1">
                  <input type="date" value={customFrom} max={customTo} onChange={e => { setCustomFrom(e.target.value); setDatePreset(0); }}
                    className="flex-1 px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                  <span className="text-xs text-gray-400">to</span>
                  <input type="date" value={customTo} min={customFrom} max={toLocalDateStr()} onChange={e => { setCustomTo(e.target.value); setDatePreset(0); }}
                    className="flex-1 px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1">{lookbackDays} days</div>
            </div>
            <div className="md:col-span-1 flex items-end gap-2">
              <button onClick={fetchActivity} disabled={loading || !selectedSpace}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-all">
                {loading ? <><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Loading...</> : <><FontAwesomeIcon icon={faRotate} className="h-4 w-4" /> Fetch</>}
              </button>
              {data && (
                <button onClick={handleGeneratePdf} disabled={generatingPdf}
                  title={pdfSaved ? 'PDF saved to Reports' : 'Generate PDF Report'}
                  className={`px-3 py-2.5 text-sm font-medium rounded-lg disabled:opacity-50 flex items-center gap-1.5 transition-all border ${
                    pdfSaved
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-100 dark:bg-slate-700/30 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                  }`}>
                  {generatingPdf ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faFileLines} className="h-4 w-4" />}
                  {pdfSaved ? 'Saved' : 'PDF'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          error === 'SCOPE_ERROR' || error === 'NOT_CONNECTED' ? (
            <div className="flex flex-col items-center gap-3 p-6 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
              <FontAwesomeIcon icon={faCircleExclamation} className="h-6 w-6 text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                  {error === 'SCOPE_ERROR' ? 'Confluence token missing required scopes' : 'Not connected to Confluence'}
                </p>
                <p className="text-xs text-amber-600/80 dark:text-amber-400/70 mt-1">
                  {error === 'SCOPE_ERROR'
                    ? 'Your current OAuth token was issued without Confluence API scopes. Disconnect and reconnect Confluence in Settings to refresh permissions.'
                    : 'Connect your Atlassian account via Settings → Confluence.'}
                </p>
              </div>
              <button
                onClick={() => {
                  if (onOpenSettings) { onOpenSettings(); }
                  else { window.location.hash = '#settings'; }
                }}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium rounded-lg transition-colors">
                <FontAwesomeIcon icon={faGear} className="h-3.5 w-3.5" /> Open Settings
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )
        )}

        {/* Indicators */}
        {data && (
          <ConfluenceIndicators
            data={data} spaceKey={selectedSpace}
            triggerCalculate={indicatorTrigger} lookbackDays={lookbackDays}
          />
        )}

        {/* Charts */}
        {data && (
          <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <button onClick={() => setShowCharts(v => !v)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Charts & Analytics</span>
                <span className="text-[10px] text-gray-400 dark:text-slate-500">({data.events.length} events, {data.uniqueContributors} contributors)</span>
              </div>
              <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-slate-400 transition-transform ${showCharts ? 'rotate-180' : ''}`} />
            </button>
            {showCharts && (
              <div className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* Daily activity */}
                <div className="xl:col-span-2 bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faArrowTrendUp} className="h-3.5 w-3.5 text-emerald-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Daily Activity</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={180} minWidth={0}>
                    <AreaChart data={data.dailyCounts}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                      <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} allowDecimals={false} />
                      <Tooltip contentStyle={ts} />
                      <Area type="monotone" dataKey="edits" name="Edits" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="created" name="Created" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                {/* By contributor */}
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5 text-purple-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">By Contributor</h3>
                  </div>
                  <div style={{ height: Math.max(180, barData.length * 32) }}>
                    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                      <BarChart data={barData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} allowDecimals={false} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} width={80} />
                        <Tooltip contentStyle={ts} labelFormatter={(l: string) => barData.find(a => a.name === l)?.fullName ?? l} />
                        <Bar dataKey="pages" name="Pages" fill="#a855f7" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                {/* Pie + depth */}
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faChartPie} className="h-3.5 w-3.5 text-amber-400" />
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white">New vs Edited</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={140} minWidth={0}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={55}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '9px' }}>
                          {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                        </Pie>
                        <Tooltip contentStyle={ts} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {depthData.length > 1 && (
                    <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <FontAwesomeIcon icon={faChartBar} className="h-3.5 w-3.5 text-cyan-400" />
                        <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Edit Depth</h3>
                      </div>
                      <ResponsiveContainer width="100%" height={120} minWidth={0}>
                        <BarChart data={depthData}>
                          <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                          <YAxis tick={{ fontSize: 9, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} allowDecimals={false} />
                          <Tooltip contentStyle={ts} />
                          <Bar dataKey="count" name="Pages" radius={[4, 4, 0, 0]}>
                            {depthData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activity Feed */}
        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-3">
                <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Summary</h3>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    ['Events', data.events.length],
                    ['Pages', data.totalPages],
                    ['Contributors', data.uniqueContributors],
                    ['Created', data.events.filter(e => e.eventType === 'page_created').length],
                    ['Edited', data.events.filter(e => e.eventType === 'page_edited').length],
                    ['Comments', data.events.filter(e => e.eventType === 'comment_added').length],
                  ] as [string, number][]).map(([label, val]) => (
                    <div key={label} className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-gray-900 dark:text-white">{val}</div>
                      <div className="text-[10px] text-gray-400 dark:text-slate-500">{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Search */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4">
                <div className="relative">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
                    placeholder="Search pages..."
                    className="w-full pl-8 pr-3 py-2 text-xs bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-2">
                <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">Event Types</h3>
                <div className="space-y-1">
                  {[
                    ['page_created', 'Created', 'emerald'],
                    ['page_edited', 'Edited', 'blue'],
                    ['minor_edit', 'Minor Edit', 'slate'],
                    ['comment_added', 'Comment', 'purple'],
                  ].map(([type, label, color]) => {
                    const count = data.events.filter(e => e.eventType === type).length;
                    const isActive = (type === 'page_created' && showCreated) || 
                                   (type === 'page_edited' && showEdits) ||
                                   (type === 'minor_edit' && showEdits) ||
                                   (type === 'comment_added' && showEdits);
                    const onClick = () => {
                      if (type === 'page_created') setShowCreated(v => !v);
                      else if (type !== 'page_created') setShowEdits(v => !v);
                    };
                    return (
                      <button key={type} onClick={onClick} disabled={count === 0}
                        className={`w-full text-xs py-1.5 px-2 rounded-lg border transition-colors flex items-center justify-between ${
                          isActive
                            ? `bg-${color}-500/20 text-${color}-700 dark:text-${color}-300 border-${color}-500/30`
                            : count === 0
                            ? 'text-gray-300 dark:text-slate-600 border-gray-200 dark:border-slate-700 cursor-not-allowed'
                            : 'text-gray-400 border-gray-200 dark:border-slate-600 hover:text-gray-900 dark:hover:text-white'
                        }`}>
                        <span>{label}</span>
                        <span className="text-[10px] opacity-60">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contributors */}
              <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 space-y-1">
                <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5" /> Contributors
                </h3>
                <button onClick={() => setFilterAuthor('')}
                  className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${!filterAuthor ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700/50'}`}>
                  All contributors ({data.authors.reduce((s, a) => s + a.count, 0)})
                </button>
                {data.authors.map(a => (
                  <button key={a.id} onClick={() => setFilterAuthor(prev => prev === a.name ? '' : a.name)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors flex items-center justify-between ${filterAuthor === a.name ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700/50'}`}>
                    <span className="truncate">{a.name}</span>
                    <span className="text-[10px] ml-1 flex-shrink-0 opacity-60">{a.count}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="lg:col-span-3 space-y-3">
              {groupedByDate.length === 0 ? (
                <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-8 text-center">
                  <FontAwesomeIcon icon={faBook} className="h-8 w-8 text-gray-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-slate-400">No page activity matches your filters</p>
                </div>
              ) : groupedByDate.map(([dateKey, events]) => {
                const isExpanded = expandedDates.has(dateKey);
                const label = new Date(dateKey + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                return (
                  <div key={dateKey} className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
                    <button onClick={() => toggleDate(dateKey)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendarDays} className="h-3.5 w-3.5 text-blue-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{label}</span>
                        <span className="text-xs text-gray-400 dark:text-slate-500">{events.length} page{events.length !== 1 ? 's' : ''}</span>
                      </div>
                      <FontAwesomeIcon icon={faChevronRight} className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="border-t border-gray-100 dark:border-slate-700/30 divide-y divide-gray-100 dark:divide-slate-700/30">
                        {events.map((e, idx) => {
                          const rowKey = `${dateKey}_${idx}`;
                          const isSelected = selectedPage?.rowKey === rowKey;
                          return (
                            <div key={rowKey}>
                              <div
                                onClick={() => setSelectedPage(isSelected ? null : { pageId: e.pageId, spaceKey: e.spaceKey, rowKey })}
                                className={`px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-slate-700/10 transition-colors cursor-pointer ${isSelected ? 'bg-blue-500/5' : ''}`}
                              >
                                {/* Event type icon */}
                                <div className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                                  e.eventType === 'page_created' ? 'bg-emerald-500/15 text-emerald-500' :
                                  e.eventType === 'comment_added' ? 'bg-purple-500/15 text-purple-500' :
                                  e.eventType === 'minor_edit' ? 'bg-slate-500/15 text-slate-400' :
                                  'bg-blue-500/15 text-blue-500'
                                }`}>
                                  {e.eventType === 'page_created' && <FontAwesomeIcon icon={faCirclePlus} className="h-3 w-3" />}
                                  {e.eventType === 'comment_added' && <FontAwesomeIcon icon={faComment} className="h-3 w-3" />}
                                  {e.eventType === 'minor_edit' && <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />}
                                  {e.eventType === 'page_edited' && <FontAwesomeIcon icon={faPenToSquare} className="h-3 w-3" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <span className="text-sm text-gray-900 dark:text-white font-medium truncate block">
                                        {e.pageTitle}
                                      </span>
                                      {/* Action label */}
                                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide mt-0.5 ${
                                        e.eventType === 'page_created' ? 'text-emerald-600 dark:text-emerald-400' :
                                        e.eventType === 'comment_added' ? 'text-purple-600 dark:text-purple-400' :
                                        e.eventType === 'minor_edit' ? 'text-slate-400' :
                                        'text-blue-600 dark:text-blue-400'
                                      }`}>
                                        {e.eventType === 'page_created' && 'Page created'}
                                        {e.eventType === 'page_edited' && 'Page edited'}
                                        {e.eventType === 'minor_edit' && 'Minor edit'}
                                        {e.eventType === 'comment_added' && (e.commentType === 'inline' ? 'Inline comment' : 'Footer comment')}
                                        {e.versionMessage && (
                                          <span className="normal-case font-normal text-gray-500 dark:text-slate-400">— {e.versionMessage}</span>
                                        )}
                                      </span>
                                      {/* Comment body excerpt */}
                                      {e.commentBody && (
                                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 line-clamp-2 italic">
                                          "{e.commentBody}"
                                        </p>
                                      )}
                                    </div>
                                    <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                      e.eventType === 'page_created' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' :
                                      e.eventType === 'comment_added' ? 'bg-purple-500/15 text-purple-600 dark:text-purple-400' :
                                      'bg-slate-500/15 text-slate-500 dark:text-slate-400'
                                    }`}>
                                      {e.eventType === 'comment_added'
                                        ? (e.commentType === 'inline' ? 'inline' : 'footer')
                                        : `v${e.version}`}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-500 dark:text-slate-400">{e.authorName}</span>
                                    <span className="text-gray-300 dark:text-slate-600">·</span>
                                    <span className="text-xs text-gray-400 dark:text-slate-500">
                                      {new Date(e.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {isSelected && e.eventType === 'comment_added' && (
                                <CommentEventDetail event={e} />
                              )}
                              {isSelected && (
                                <ConfluencePageDetail
                                  pageId={e.pageId}
                                  spaceKey={e.spaceKey}
                                  version={e.eventType !== 'comment_added' && e.version && typeof e.version === 'number' ? e.version : undefined}
                                  onClose={() => setSelectedPage(null)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
